import React from 'react';
import { Button, message, Modal, Row, Space, Spin, Card } from 'antd';
import { DeleteOutlined, AppstoreOutlined, CarOutlined, DollarOutlined } from '@ant-design/icons';
import to from 'await-to-js';
import { useHistory } from 'react-router-dom';
import AntEditingTable from '../../SharedComponents/RSEditableTable/AntEditingTable';
import {
  getGroupedDraftOperations,
  getOperationSubmitLoading,
  removeGroup,
  removeGroupedDraftOperation,
  submitGroupedOperations,
  updateGroupedDraftOperations,
} from '../../store/operations.store';
import PageLayout from '../../SharedComponents/PageLayout';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { showMapView } from '../../store/app-ui.store';
import getOperationEditingColumns from '../../SharedComponents/operation-editing-columns';

const OperationEditingWithVehiclePage = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const data = useAppSelector(getGroupedDraftOperations);
  const submitLoading = useAppSelector(getOperationSubmitLoading);

  const summary = {
    locations: data.reduce((p, c) => p + c.children.length, 0),
    totalVehicleCapacity: data.reduce((p, c) => p + Number(c.parentCapacity ?? 0), 0),
    totalRouteDistance: data.reduce((p, c) => p + Number(c.routeDistance ?? 0), 0),
    totalRouteLoad: data.reduce((p, c) => p + Number(c.routeLoad ?? 0), 0),
    totalValue: data.reduce(
      (p, c) => p + c.children.reduce((sum: number, child: any) => sum + Number(child.value ?? 0), 0),
      0,
    ),
    initialLoad: data.reduce((p, c) => {
      Object.entries(c.initialLoad ?? {}).forEach(([item, qty]) => {
        p[item] = (p[item] ?? 0) + Number(qty);
      });
      return p;
    }, {} as Record<string, number>),
  };

  const openViewAllLocationMap = () => {
    const locations = data.reduce((p: any[], c) => {
      p.push(...c.children.map((loc) => ({ ...loc.location, mapColor: c.mapColor })));
      return p;
    }, []);
    dispatch(showMapView({ show: true, locations }));
  };

  const onSubmit = async () => {
    const [err] = await to(dispatch(submitGroupedOperations(0)).unwrap());
    if (err) {
      return Modal.error({
        content: (err as any)?.message ?? 'Something went wrong',
        title: 'Submission Failed',
      });
    }
    history.push('/operations');
    return message.success('Successfully submitted the route');
  };

  const updateLocations = (
    key: number,
    lng: number,
    lat: number,
    locationId: string,
    name: string,
    address: string,
    clearErrors?: boolean,
    waiting_time?: number,
  ) => {
    dispatch(
      updateGroupedDraftOperations({ index: key, key: 'location', data: { lng: Number(lng), lat: Number(lat) } }),
    );
    dispatch(updateGroupedDraftOperations({ index: key, key: 'address', data: address }));
    dispatch(updateGroupedDraftOperations({ index: key, key: 'locationId', data: locationId }));
    dispatch(updateGroupedDraftOperations({ index: key, key: 'name', data: name, clearErrors }));
    dispatch(updateGroupedDraftOperations({ index: key, key: 'waiting_time', data: waiting_time || 0 }));
  };

  return (
    <PageLayout title="Update Location Allocations for Vehicles">
      <Spin spinning={submitLoading} tip={submitLoading ? 'Please wait, we are processing...' : undefined}>
        <Row>
          <div className="d-flex jc-space-around w-100 pd-b-10">
            <div className="ops-metrics-grid">
              {[
                {
                  label: 'Total Vehicle Capacity',
                  value: summary.totalVehicleCapacity,
                  icon: <CarOutlined />,
                },
                {
                  label: 'Total Value',
                  value: summary.totalValue,
                  icon: <DollarOutlined />,
                },
                {
                  label: 'Total Distance',
                  value: `${(summary.totalRouteDistance / 1000).toFixed(1)} km`,
                  icon: <CarOutlined />,
                },
                {
                  label: 'Total Route Load',
                  value: summary.totalRouteLoad,
                  icon: <AppstoreOutlined />,
                },
              ].map(({ label, value, icon }) => (
                <Card key={label} bodyStyle={{ padding: '10px 12px' }}>
                  <div className="ops-metrics-grid__header">
                    <span className="ops-metrics-grid__label">{label}</span>
                    <span className="ops-metrics-grid__icon">{icon}</span>
                  </div>
                  <div className="ops-metrics-grid__value">{value}</div>
                </Card>
              ))}
              {Object.keys(summary.initialLoad).length > 0 && (
                <Card bodyStyle={{ padding: '10px 12px' }}>
                  <div className="ops-metrics-grid__header">
                    <span className="ops-metrics-grid__label">Initial Load</span>
                    <span className="ops-metrics-grid__icon">
                      <AppstoreOutlined />
                    </span>
                  </div>
                  <div className="ops-metrics-grid__value">
                    {Object.entries(summary.initialLoad).map(([item, qty]) => (
                      <div key={item} style={{ fontSize: 12 }}>
                        <span style={{ color: '#52c41a', marginRight: 4 }}>↑</span>
                        {item}: <span style={{ fontWeight: 600 }}>{qty}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
            <Space direction="horizontal">
              <Button onClick={openViewAllLocationMap} block>
                Show All Locations
              </Button>
              <Button type="primary" onClick={onSubmit} loading={submitLoading}>
                Submit
              </Button>
            </Space>
          </div>
          <AntEditingTable
            className="grouped-table"
            rowClassName={(record) => (record.type === 'parent' ? 'vehicle' : '')}
            extraButtons={(key: string, rowData) => (
              <Button
                icon={<DeleteOutlined />}
                danger
                onClick={() => {
                  if (rowData.type === 'parent') {
                    dispatch(removeGroup(rowData.key));
                  } else {
                    dispatch(removeGroupedDraftOperation(key));
                  }
                }}
                disabled={rowData.isStartingRow || rowData.isEndingRow}
              />
            )}
            columns={getOperationEditingColumns(updateLocations, dispatch)}
            dataSource={data.map((group) => ({
              ...group,
              children: [...group.children].sort((a, b) => {
                if (a.isStartingRow && a.isEndingRow) {
                  return -1;
                }
                if (b.isStartingRow && b.isEndingRow) {
                  return 1;
                }
                if (a.isStartingRow) {
                  return -1;
                }
                if (b.isStartingRow) {
                  return 1;
                }
                if (a.isEndingRow) {
                  return 1;
                }
                if (b.isEndingRow) {
                  return -1;
                }
                return (a.wayPointIndex ?? 0) - (b.wayPointIndex ?? 0);
              }),
            }))}
            scroll={{ y: 480 }}
            styles={{}}
          />
        </Row>
      </Spin>
    </PageLayout>
  );
};

export default OperationEditingWithVehiclePage;
